<script setup lang="ts">
import { useShortsFeedStore } from '~/store/shortsFeedStore';
import { useVideoHistoryStore } from '~/store/videoHistoryStore';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { storeToRefs } from 'pinia';

import { Button } from '~/components/ui/button';

import IconMaterialSymbolsLightPlayArrowRounded from '~icons/material-symbols-light/play-arrow-rounded';

const emit = defineEmits<{
	(e: 'open-feed'): void;
}>();

function normalizeThumbUrl(url: string | null | undefined): string | null {
	if (!url) return null;
	return url.replace(/\.(webp|png|jpeg|gif)(?=\?|#|$)/i, '.jpg');
}

const getCookieData = useCookieData();
const { user } = useUserSession();

const { data: ssrThumbnailUrl } = useAsyncData(
	'header-video-thumbnail',
	async () => {
		const [result] = await nuxtPropertiesRepository.getShorts(
			{ page: 1, perPage: 1, markUserWishlistsId: user.value?.id },
			getCookieData()
		);
		const thumbnail = result?.properties?.[0]?.shorts?.[0]?.thumbnail;
		return normalizeThumbUrl(thumbnail);
	}
);

const feedStore = useShortsFeedStore();
const historyStore = useVideoHistoryStore();
const { videos, isPreloaded, allWatched } = storeToRefs(feedStore);
const { historyItems, isHistoryLoaded } = storeToRefs(historyStore);

const firstVideo = computed(() => {
	const feedVideo = videos.value[0];
	if (feedVideo) return feedVideo;

	const historyVideo = historyItems.value?.[0];
	if (historyVideo) {
		return {
			property: historyVideo.property,
			videoUrl: historyVideo.videoUrl,
			thumbnailUrl: historyVideo.thumbnailUrl
		};
	}
	return null;
});

const thumbnailUrl = computed(() => {
	if (firstVideo.value) {
		return normalizeThumbUrl(firstVideo.value.thumbnailUrl);
	}
	return ssrThumbnailUrl.value;
});

const hasVideo = computed(
	() =>
		!isPreloaded.value ||
		!isHistoryLoaded.value ||
		!!firstVideo.value ||
		allWatched.value
);

function handleClick() {
	emit('open-feed');
}
</script>

<template>
	<Button
		v-if="hasVideo"
		variant="transparent"
		size="xs"
		:is-client-only="false"
		class="group relative flex items-center gap-2 overflow-hidden rounded-full border-none bg-transparent transition-[transform,background-color,box-shadow] active:scale-95 sm:bg-white/10 sm:pr-3 sm:hover:bg-white/20 sm:hover:shadow-lg sm:hover:shadow-white/10"
		aria-label="Ver vídeos em destaque"
		@click="handleClick"
	>
		<div class="relative">
			<div
				class="absolute -inset-1 animate-pulse rounded-full bg-white/30 duration-[2s]"
			/>

			<div
				class="relative size-11 overflow-hidden rounded-full border-2 border-white/60 transition-all group-hover:border-white sm:size-10"
			>
				<NuxtImg
					v-if="thumbnailUrl"
					:src="thumbnailUrl"
					alt="Preview do vídeo"
					width="44"
					height="44"
					format="webp"
					quality="80"
					class="size-full object-cover"
					:preload="{ fetchPriority: 'high' }"
					fetchpriority="high"
					decoding="async"
				/>
				<div v-else class="size-full bg-neutral-800" />

				<div
					class="absolute inset-0 flex items-center justify-center bg-black/20"
				>
					<IconMaterialSymbolsLightPlayArrowRounded
						class="size-5 text-white drop-shadow"
					/>
				</div>
			</div>
		</div>

		<span class="hidden text-sm font-medium text-white sm:inline">
			Descubra
		</span>
	</Button>
</template>
